import { useState, useEffect } from "react";
import { useNotifications } from "@/context/NotificationContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  FileText,
  Receipt,
  FileSignature,
  Upload,
  CheckSquare,
  Calculator,
  X,
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CATEGORY_ICONS = {
  invoice: Receipt,
  contract: FileSignature,
  document: Upload,
  task: CheckSquare,
  tax: Calculator,
  default: Bell
};

const TYPE_STYLES = {
  success: { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-50" },
  error: { icon: AlertCircle, color: "text-red-500", bg: "bg-red-50" },
  warning: { icon: AlertTriangle, color: "text-yellow-500", bg: "bg-yellow-50" },
  info: { icon: Info, color: "text-blue-500", bg: "bg-blue-50" }
};

export default function NotificationCenter() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [actionTasks, setActionTasks] = useState([]);
  const { 
    notifications, 
    unreadCount,
    actionTasksCount,
    markAsRead, 
    markAllAsRead, 
    clearAll, 
    removeNotification 
  } = useNotifications();

  // Fetch action tasks when opened
  useEffect(() => {
    const fetchActionTasks = async () => {
      if (!open) return;
      const token = localStorage.getItem("auth_token");
      if (!token) return;
      
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/action-tasks`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setActionTasks(data.filter(t => t.status === "pending"));
        }
      } catch (err) {
        console.error("Failed to fetch action tasks:", err);
      }
    };
    fetchActionTasks();
  }, [open]);

  const totalCount = unreadCount + actionTasksCount;

  const handleNotificationClick = (notif) => {
    if (!notif.read) {
      markAsRead(notif.id);
    }
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
      setOpen(false);
    }
  };

  const handleActionTaskClick = (task) => {
    navigate("/dashboard/tasks");
    setOpen(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-gray-500" />
          {totalCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs"
            >
              {totalCount > 9 ? "9+" : totalCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-900">Notifications</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead}
                className="text-xs text-gray-500 h-7"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearAll}
                className="text-xs text-gray-500 h-7"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          {/* Action Tasks Section */}
          {actionTasks.length > 0 && (
            <div className="border-b border-amber-200">
              <div className="p-3 bg-amber-50">
                <h4 className="text-xs font-semibold text-amber-800 uppercase flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Action Required ({actionTasks.length})
                </h4>
              </div>
              {actionTasks.map((task) => (
                <div
                  key={task.task_id}
                  onClick={() => handleActionTaskClick(task)}
                  className="p-4 cursor-pointer hover:bg-amber-50/50 transition-colors border-b border-amber-100"
                >
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Upload className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{task.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{task.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-amber-600 font-medium">{task.action_label || "Take Action"}</span>
                        {task.due_date && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="text-xs text-gray-400">Due {new Date(task.due_date).toLocaleDateString()}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {notifications.length === 0 && actionTasks.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notif) => {
                const typeStyle = TYPE_STYLES[notif.type] || TYPE_STYLES.info;
                const TypeIcon = typeStyle.icon;
                const CategoryIcon = CATEGORY_ICONS[notif.category] || CATEGORY_ICONS.default;

                return (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !notif.read ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`w-8 h-8 rounded-full ${typeStyle.bg} flex items-center justify-center flex-shrink-0`}>
                        <TypeIcon className={`w-4 h-4 ${typeStyle.color}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${!notif.read ? "text-gray-900" : "text-gray-700"}`}>
                            {notif.title}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notif.id);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {notif.message}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <CategoryIcon className="w-3 h-3" />
                            <span className="capitalize">{notif.category || "general"}</span>
                          </div>
                          <span className="text-gray-300">•</span>
                          <span className="text-xs text-gray-400">{formatTime(notif.timestamp)}</span>
                          {!notif.read && (
                            <>
                              <span className="text-gray-300">•</span>
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
